import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, ShieldCheck, AlertTriangle } from "lucide-react";
import { HIDDEN_TRUTH as T } from "../data/hiddenTruth";
import { LOGO } from "../mock";
import { useSeo } from "../hooks/useSeo";

const Bold = ({ text }) =>
  text.split(/(\*\*[^*]+\*\*)/).map((part, i) =>
    part.startsWith("**") ? <strong key={i} className="text-[#1B4332]">{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>
  );

const ThemCard = () => (
  <div className="rounded-3xl border-2 border-[#f3c9c9] bg-white p-6 sm:p-8" data-testid="hidden-truth-them">
    <div className="mb-3 flex items-center gap-2 text-[#C62828]"><AlertTriangle size={20} /><span className="font-display text-xs font-bold uppercase tracking-[0.25em]">The Nasty Extras</span></div>
    <h2 className="font-display text-2xl font-bold uppercase text-[#1B2a21] sm:text-3xl">{T.them.title}</h2>
    <p className="mt-3 text-[15px] leading-relaxed text-[#3d4f46]">{T.them.body}</p>
    <hr className="my-5 border-[#f3c9c9]" />
    <p className="text-[15px] leading-relaxed text-[#C62828]" data-testid="hidden-truth-ingredients">{T.them.ingredients}</p>
  </div>
);

const UsCard = () => (
  <div className="rounded-3xl border-2 border-[#95D5B2] bg-white p-6 sm:p-8" data-testid="hidden-truth-us">
    <div className="mb-3 flex items-center gap-2 text-[#2D6A4F]"><ShieldCheck size={20} /><span className="font-display text-xs font-bold uppercase tracking-[0.25em]">Nasty Free</span></div>
    <h2 className="font-display text-2xl font-bold uppercase text-[#2D6A4F] sm:text-3xl">{T.us.title}</h2>
    <img src={LOGO} alt="British Supplements" className="my-5 h-24 w-auto" />
    <p className="italic text-[#3d4f46]">{T.us.question}</p>
    <p className="mt-2 font-display text-2xl font-bold uppercase text-[#1B4332]">{T.us.answer}</p>
    <hr className="my-5 border-[#d8ede0]" />
    <div className="space-y-4 text-[15px] leading-relaxed text-[#3d4f46]">
      {T.us.body.map((p, i) => <p key={i}><Bold text={p} /></p>)}
    </div>
  </div>
);

const CrownJewels = () => (
  <section className="bg-[#1B4332] py-14 text-white" data-testid="crown-jewels-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <div className="mb-2 flex items-center gap-2 text-[#95D5B2]"><Crown size={22} /><span className="font-display text-xs font-bold uppercase tracking-[0.3em]">Buy British?</span></div>
      <h2 className="font-display text-3xl font-bold uppercase leading-tight sm:text-4xl">{T.crown.title}</h2>
      <p className="mt-4 max-w-4xl font-semibold leading-relaxed text-[#e3f1e8]">{T.crown.lead}</p>
      <p className="mt-3 max-w-4xl font-semibold leading-relaxed text-[#B7E4C7]">{T.crown.note}</p>
      <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="crown-jewels-list">
        {T.crown.list.map((item) => (
          <li key={item} className="flex items-start gap-3 rounded-xl bg-white/10 px-4 py-3 text-sm leading-snug">
            <Crown size={16} className="mt-0.5 shrink-0 text-[#F4C430]" /> {item}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

export default function HiddenTruth() {
  useSeo({
    title: "The Hidden Truth – What's Really In Your Supplements",
    description: "See the nasty extras Big Pharma adds to supplements (magnesium stearate, titanium dioxide, talc and more) and why British Supplements adds nothing.",
  });
  return (
    <div data-testid="hidden-truth-page">
      <section className="bg-[#C62828] px-4 py-14 text-center text-white sm:px-6">
        <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#ffd6d6]">The Hidden Truth</p>
        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl lg:text-6xl">{T.heading}</h1>
        <div className="mx-auto mt-5 max-w-3xl space-y-2 text-[15px] leading-relaxed text-[#fff1f1]">
          {T.intro.map((p) => <p key={p}>{p}</p>)}
        </div>
      </section>
      <section className="bg-[#eef7f1] py-14">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 lg:grid-cols-2">
          <ThemCard />
          <UsCard />
        </div>
      </section>
      <CrownJewels />
      <section className="bg-[#eef7f1] py-12 text-center">
        <Link to="/shop" data-testid="hidden-truth-shop-btn" className="inline-flex items-center gap-2 rounded-full bg-[#2D6A4F] px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-[#1B4332]">
          Shop Nasty Free Supplements <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}
